import { getLocalizedMessage } from '@figma/i18n/get_localized_message'
import { t } from '@figma/i18n/translation'
import type { I18nServerObject } from '@figma/i18n/types'
import { generalAnErrorOccurredWhilePerformingThatAction } from '@figma/i18n-dictionaries/en/web/general'
import { atomStore } from '@figma/jotai'
import { ExperimentsApi } from '@figma/share-statsig'
import { type AsyncAction, type ThunkCreator, thunkCreator } from '@web/lib/action_helpers'
import { optimisticRequest } from '@web/lib/optimistic_request'
import { extractXhrSupportRequestId } from '@web/lib/xhr_request_metadata'
import type { JSX } from 'react'
import type * as redux from 'redux'

import { flashesAtom } from './atoms'
import { type FlashInitialOptions, type FlashOptions, FlashStatus } from './types'

var flashId = 0

/**
 * Given a server-generated string or JSON-parsed i18n object, returns a localized string
 * if possible. Otherwise, returns the fallback text or server-generated string.
 *
 * When localizing surfaces that used to pass information from Sinatra to React in plaintext
 * (such as flash messages), we must identify whether the server is sending an i18n object while
 * maintaining backward-compatibility with unlocalized plaintext messages.
 *
 * @param i18n_or_raw_text An i18n object or raw text passed from the server to the frontend.
 * @returns a localized string if possible. Otherwise, returns the plaintext fallback string or
 * string passed from the server
 */
function getPlaintextOrLocalizedMessage(i18n_or_raw_text: I18nServerObject | string) {
  if (typeof i18n_or_raw_text === 'string') {
    return i18n_or_raw_text
  } else {
    return getLocalizedMessage({ data: { i18n: i18n_or_raw_text } }, i18n_or_raw_text.fallback_text)
  }
}

const showFlashWithStatus = /*#__PURE__*/ thunkCreator<
  {
    id: number
    message: string | JSX.Element
    status: FlashStatus
  } & FlashOptions,
  number
>((store, payload) => {
  // remove any duplicate flashes if they exist
  const flashes = atomStore.get(flashesAtom)
  for (const id in flashes) {
    const flash = flashes[id]
    if (
      flash?.message === payload.message &&
      flash.status === payload.status &&
      flash.supportRequestId === payload.supportRequestId
    ) {
      store.dispatch(removeFlash({ id: parseInt(id) }))
      break
    }
  }

  const id = payload.id
  const timeout =
    payload.timeout && payload.timeout > FLASH_TIMEOUT ? payload.timeout : FLASH_TIMEOUT
  const persistent =
    payload.supportRequestId && ExperimentsApi.getFeatureGate('srids_in_error_flashes')
      ? true
      : payload.persistent
  if (!persistent) {
    setTimeout(() => {
      store.dispatch(removeFlash({ id }))
    }, timeout)
  }
  store.dispatch(addFlash(persistent === payload.persistent ? payload : { ...payload, persistent }))

  return id
})

export const flashOnFailure: ThunkCreator<{
  promise: Promise<unknown>
  fallbackError: string
  supportRequestId?: string
  debugName?: string
}> = /*#__PURE__*/ thunkCreator<{
  promise: Promise<unknown>
  fallbackError: string
  supportRequestId?: string
  debugName?: string
}>((store, payload) => {
  payload.promise.catch((error: unknown) => {
    const supportRequestId = extractXhrSupportRequestId(error) ?? payload.supportRequestId
    const showFailure = (message: string) =>
      store.dispatch(showErrorFlash(message, { supportRequestId, debugName: payload.debugName }))
    try {
      if (
        !error ||
        typeof error !== 'object' ||
        !('response' in error) ||
        typeof error.response !== 'string'
      ) {
        throw new Error('No error response to localize')
      }
      const response = JSON.parse(error.response)
      const message = getLocalizedMessage(
        { ...response, data: { ...response.data, i18n: response.i18n } },
        response.message,
      )
      showFailure(message)
    } catch (e) {
      showFailure(payload.fallbackError || t(generalAnErrorOccurredWhilePerformingThatAction))
    }
  })
})

interface OptimisticRequestWithFlashOnFailurePayload<R> {
  requestPromise: Promise<R>
  fallbackError: string
  action: redux.AnyAction
  /**
   * Where to dispatch the optimistic transaction. Middleware should pass its `next` so the
   * action isn't handled by the same middleware again. Defaults to `store.dispatch`.
   */
  next?: (action: redux.AnyAction) => redux.AnyAction
}

const optimisticRequestWithFlashOnFailureThunk = /*#__PURE__*/ thunkCreator<
  OptimisticRequestWithFlashOnFailurePayload<unknown>,
  Promise<unknown>
>((store, payload) => {
  const { requestPromise, fallbackError, action, next = store.dispatch } = payload
  store.dispatch(flashOnFailure({ promise: requestPromise, fallbackError }))
  return optimisticRequest(requestPromise, next, action)
})

/**
 * Applies `action` optimistically until `requestPromise` settles, reverting it and showing an
 * error flash if the request fails.
 */
export function optimisticRequestWithFlashOnFailure<R>(
  payload: OptimisticRequestWithFlashOnFailurePayload<R>,
): AsyncAction<Promise<R>, unknown, unknown> {
  return optimisticRequestWithFlashOnFailureThunk(payload) as AsyncAction<
    Promise<R>,
    unknown,
    unknown
  >
}

export const addFlash: ThunkCreator<
  {
    id: number
    message: string | JSX.Element
    status: FlashStatus
  } & FlashOptions
> = /*#__PURE__*/ thunkCreator<
  {
    id: number
    message: string | JSX.Element
    status: FlashStatus
  } & FlashOptions
>((_, payload) => {
  atomStore.set(flashesAtom, { ...atomStore.get(flashesAtom), [payload.id]: payload })
})

export const removeFlash: ThunkCreator<{ id: number }> = /*#__PURE__*/ thunkCreator<{
  id: number
}>((_, payload) => {
  const newState = { ...atomStore.get(flashesAtom) }
  delete newState[payload.id]
  atomStore.set(flashesAtom, newState)
})

export const removeAllFlashes: ThunkCreator<void> = /*#__PURE__*/ thunkCreator<void>(() => {
  atomStore.set(flashesAtom, {})
})

// Default time after the flash will fade away
export const FLASH_TIMEOUT = 3000
// A longer timeout to wait for the page to finish loading,
// giving a bit longer to absorb the page first before fading.
export const SERVER_SIDE_FLASH_TIMEOUT = 6000

export const initializeFlash: ThunkCreator<FlashInitialOptions['flash']> =
  /*#__PURE__*/ thunkCreator<FlashInitialOptions['flash']>((store, serverFlash) => {
    if (serverFlash) {
      if (serverFlash.error) {
        store.dispatch(
          showErrorFlash(getPlaintextOrLocalizedMessage(serverFlash.error), {
            timeout: SERVER_SIDE_FLASH_TIMEOUT,
            supportRequestId: serverFlash.supportRequestId,
            debugName: serverFlash.debugName,
          }),
        )
      } else if (serverFlash.warn) {
        store.dispatch(
          showFlash(
            getPlaintextOrLocalizedMessage(serverFlash.warn),
            SERVER_SIDE_FLASH_TIMEOUT,
            serverFlash.dark,
            serverFlash.persistent,
            serverFlash.icon,
            serverFlash.bottom,
            serverFlash.supportRequestId,
            serverFlash.debugName,
          ),
        )
      } else if (serverFlash.success) {
        store.dispatch(
          showFlash(
            getPlaintextOrLocalizedMessage(serverFlash.success),
            SERVER_SIDE_FLASH_TIMEOUT,
            serverFlash.dark,
            serverFlash.persistent,
            serverFlash.icon,
            serverFlash.bottom,
            serverFlash.supportRequestId,
            serverFlash.debugName,
          ),
        )
      }
    }
  })

export function showFlash(
  content: string | JSX.Element,
  options?: FlashOptions,
): AsyncAction<number, unknown, unknown>
export function showFlash(
  content: string | JSX.Element,
  timeout?: number,
  dark?: boolean,
  persistent?: boolean,
  icon?: string,
  bottom?: boolean,
  supportRequestId?: string,
  debugName?: string,
): AsyncAction<number, unknown, unknown>
export function showFlash(
  content: string | JSX.Element,
  timeoutOrOptions?: number | FlashOptions,
  dark?: boolean,
  persistent?: boolean,
  icon?: string,
  bottom?: boolean,
  supportRequestId?: string,
  debugName?: string,
): AsyncAction<number, unknown, unknown> {
  if (typeof timeoutOrOptions === 'object') {
    return showFlashWithStatus({
      ...timeoutOrOptions,
      message: content,
      status: FlashStatus.DEFAULT,
      id: flashId++,
    })
  }

  return showFlashWithStatus({
    message: content,
    status: FlashStatus.DEFAULT,
    timeout: timeoutOrOptions,
    dark,
    persistent,
    icon,
    bottom,
    supportRequestId,
    debugName,
    id: flashId++,
  })
}

export function showErrorFlash(
  message: string | JSX.Element,
  options?: FlashOptions,
): AsyncAction<number, unknown, unknown>
export function showErrorFlash(
  message: string | JSX.Element,
  timeout?: number,
  dark?: boolean,
  persistent?: boolean,
  icon?: string,
  bottom?: boolean,
  supportRequestId?: string,
  debugName?: string,
): AsyncAction<number, unknown, unknown>
export function showErrorFlash(
  message: string | JSX.Element,
  timeoutOrOptions?: number | FlashOptions,
  dark?: boolean,
  persistent?: boolean,
  icon?: string,
  bottom?: boolean,
  supportRequestId?: string,
  debugName?: string,
): AsyncAction<number, unknown, unknown> {
  const options =
    typeof timeoutOrOptions === 'object'
      ? {
          timeout: timeoutOrOptions.timeout,
          dark: timeoutOrOptions.dark,
          persistent: timeoutOrOptions.persistent,
          icon: timeoutOrOptions.icon,
          bottom: timeoutOrOptions.bottom,
          supportRequestId: timeoutOrOptions.supportRequestId,
          debugName: timeoutOrOptions.debugName,
        }
      : { timeout: timeoutOrOptions, dark, persistent, icon, bottom, supportRequestId, debugName }

  return showFlashWithStatus({
    message,
    status: FlashStatus.ERROR,
    ...options,
    id: flashId++,
  })
}
